/**
 * Registry of filterable / editable fields for modular bulk property edits
 * on price-list products.
 */
import { CURRENCY_OPTIONS } from '@/data/manufacturerCodes';
import {
    PRODUCT_STATUS_OPTIONS,
    formatProductStatus,
    normalizeProductStatus,
} from '@/data/productStatus';
import {
    MAIN_PRODUCT_CATEGORIES,
    getProductMainCategories,
    normalizeSystemValue,
} from '@/data/productCategories';
import {
    formatNumericInputValue,
    normalizeCostUpliftType,
    parseNumericInput,
    parseStoredCostUplifts,
    parseStoredDiscountNotes,
    ratioToPercentageInput,
    roundMoney,
} from '@/utils/priceListProductCalculator';
import { BULK_NUMERIC_INPUT_MAX_DECIMALS } from '@/utils/priceListBulkDialogHelpers';
import { includesFolded } from '@/utils/textSearch';
import { isProductDetailsDirectSaveField } from '@/utils/priceListProductDetailsKeys';

export const BULK_FIELD_GROUPS = {
    details: 'Detalii produs',
    categories: 'Sisteme',
    pricing: 'Prețuri / adaosuri',
    flags: 'Opțiuni',
};

/**
 * @typedef {Object} BulkFieldDefinition
 * @property {string} key
 * @property {string} label
 * @property {string} group
 * @property {'text'|'percent'|'boolean'|'select'|'measurementUnit'|'systems'|'number'|'manufacturer'|'chapter'} valueType
 * @property {boolean} [filterable]
 * @property {boolean} [editable]
 * @property {boolean} [needsPricingRecalc]
 * @property {boolean} [requiresPricingDetail]
 * @property {boolean} [markupRecalcPolicyA]
 * @property {Array<{title: string, value: string}>} [options]
 * @property {(item: Object) => unknown} readCurrent
 * @property {(value: unknown) => string} [formatDisplay]
 */

function asText(value) {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim();
}

function asPercentDisplay(value) {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = parseNumericInput(value);
    // Stored ratios (0.85) vs already-percentage UI values (85).
    const percent = Math.abs(numeric) <= 1.5 ? numeric * 100 : numeric;
    const rounded = roundMoney(percent, BULK_NUMERIC_INPUT_MAX_DECIMALS);
    return `${formatNumericInputValue(rounded, BULK_NUMERIC_INPUT_MAX_DECIMALS)}%`;
}

function asNumberDisplay(value, decimals = BULK_NUMERIC_INPUT_MAX_DECIMALS) {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = parseNumericInput(value);
    if (!Number.isFinite(numeric)) {
        return '—';
    }
    return formatNumericInputValue(roundMoney(numeric, decimals), decimals) || '—';
}

/**
 * Display code for a product currency (ron → RON, euro → EUR).
 * @param {unknown} currency
 * @returns {string}
 */
function currencyDisplayCode(currency) {
    const code = String(currency ?? '').trim().toLowerCase();
    if (!code) {
        return '';
    }
    const option = CURRENCY_OPTIONS.find(
        (row) => String(row.value).toLowerCase() === code,
    );
    if (option?.title) {
        return option.title;
    }
    if (code === 'euro' || code === 'eur') {
        return 'EUR';
    }
    return code.toUpperCase();
}

/**
 * Bulk preview label for preț listă furnizor: amount + currency + U.M.
 * Optional percentLabel → "55 RON / m (+10%)".
 * @param {unknown} amount
 * @param {Object} item
 * @param {{ percentLabel?: string, decimals?: number }} [options]
 * @returns {string}
 */
export function formatSupplierListPriceBulkLabel(amount, item, options = {}) {
    const decimals = options.decimals ?? 2;
    const amountLabel = asNumberDisplay(amount, decimals);
    if (amountLabel === '—') {
        return options.percentLabel || '—';
    }
    const currency = currencyDisplayCode(
        readPricingField(item, 'currency') || item?.currency || 'ron',
    );
    const unit = asText(item?.measurement_unit);
    let label = amountLabel;
    if (currency) {
        label = `${label} ${currency}`;
    }
    if (unit) {
        label = `${label} / ${unit}`;
    }
    if (options.percentLabel) {
        label = `${label} (${options.percentLabel})`;
    }
    return label;
}

/**
 * Sum of existing fixed cost-uplift rows (RON/buc), for bulk preview "current".
 * @param {Object} item
 * @returns {number|null}
 */
function readCostUpliftFixedCurrent(item) {
    const raw = readPricingField(item, 'costUplifts');
    const rows = parseStoredCostUplifts(raw);
    let sumFixed = 0;
    let found = false;
    rows.forEach((row) => {
        if (normalizeCostUpliftType(row.type) !== 'fixed') {
            return;
        }
        const amount = parseNumericInput(row.amount);
        if (!(amount > 0)) {
            return;
        }
        found = true;
        sumFixed += amount;
    });
    return found ? sumFixed : null;
}

function asBooleanDisplay(value) {
    return value ? 'Da' : 'Nu';
}

function systemsFromItem(item) {
    const fromAssignments = getProductMainCategories(item);
    if (fromAssignments.length) {
        return fromAssignments;
    }
    const detail = item?.pricing_detail || {};
    const systems = Array.isArray(detail.systems)
        ? detail.systems.map((system) => normalizeSystemValue(system)).filter(Boolean)
        : [detail.system1, detail.system2, detail.system3]
            .map((system) => normalizeSystemValue(system))
            .filter(Boolean);
    if (systems.length) {
        return systems;
    }
    return (item?.categories || [])
        .map((category) => normalizeSystemValue(category))
        .filter(Boolean);
}

function subcategoryForSystem(item, system) {
    const normalized = normalizeSystemValue(system);
    if (!normalized) {
        return '';
    }
    const assignments = item?.category_assignments || [];
    for (const assignment of assignments) {
        const main = normalizeSystemValue(
            assignment?.main_category?.name || assignment?.main_category,
        );
        if (main === normalized) {
            return asText(assignment?.sub_category?.name || assignment?.sub_category);
        }
    }
    const detail = item?.pricing_detail || {};
    const systems = Array.isArray(detail.systems) ? detail.systems : [];
    const subs = Array.isArray(detail.systemSubcategories)
        ? detail.systemSubcategories
        : [];
    const index = systems.findIndex(
        (entry) => normalizeSystemValue(entry) === normalized,
    );
    if (index >= 0) {
        return asText(subs[index]);
    }
    return '';
}

function readPricingField(item, camelKey, snakeKey = null) {
    const detail = item?.pricing_detail || {};
    if (detail[camelKey] !== undefined && detail[camelKey] !== null && detail[camelKey] !== '') {
        return detail[camelKey];
    }
    const pricings = Array.isArray(detail.furnizorPricings) ? detail.furnizorPricings : [];
    if (pricings.length) {
        const defaultRow = pricings.find((row) => row?.isDefault) || pricings[0];
        if (
            defaultRow
            && defaultRow[camelKey] !== undefined
            && defaultRow[camelKey] !== null
            && defaultRow[camelKey] !== ''
        ) {
            return defaultRow[camelKey];
        }
    }
    if (snakeKey && item?.[snakeKey] !== undefined && item?.[snakeKey] !== null && item?.[snakeKey] !== '') {
        return item[snakeKey];
    }
    if (item?.[camelKey] !== undefined && item?.[camelKey] !== null && item?.[camelKey] !== '') {
        return item[camelKey];
    }
    return null;
}

/**
 * Read a list exchange-rate key from the product's pricing settings snapshot.
 * @param {Object} item
 * @param {'exchangeRateEur'|'exchangeRatePln'|'exchangeRateUsd'} rateKey
 * @returns {unknown}
 */
function readExchangeRateFromItem(item, rateKey) {
    const detail = item?.pricing_detail || {};
    const snap = detail.pricingSettingsSnapshot
        || detail.pricing_settings_snapshot
        || {};
    if (snap[rateKey] !== undefined && snap[rateKey] !== null && snap[rateKey] !== '') {
        return snap[rateKey];
    }
    const pricings = Array.isArray(detail.furnizorPricings) ? detail.furnizorPricings : [];
    const defaultRow = pricings.find((row) => row?.isDefault) || pricings[0];
    const rowSnap = defaultRow?.pricingSettingsSnapshot
        || defaultRow?.pricing_settings_snapshot
        || {};
    if (rowSnap[rateKey] !== undefined && rowSnap[rateKey] !== null && rowSnap[rateKey] !== '') {
        return rowSnap[rateKey];
    }
    return null;
}

/**
 * Resolve a pricing percent field as a ratio (0.7 for 70%). Returns null when missing.
 * @param {Object} item
 * @param {string} camelKey
 * @param {string|null} [snakeKey]
 * @returns {number|null}
 */
function resolveItemPercentRatio(item, camelKey, snakeKey = null) {
    const raw = readPricingField(item, camelKey, snakeKey);
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    const numeric = parseNumericInput(raw);
    if (!Number.isFinite(numeric)) {
        return null;
    }
    // API stores ratios; UI/percentage leftovers > 1.5 are treated as percent points.
    return Math.abs(numeric) <= 1.5 ? numeric : numeric / 100;
}

/**
 * Resolve markup referință as a ratio (0.7 for 70%). Returns null when missing.
 * @param {Object} item
 * @returns {number|null}
 */
function resolveItemMarkupRatio(item) {
    return resolveItemPercentRatio(item, 'markupReference', 'markup_reference');
}

/**
 * Resolve adaos actual as a ratio (0.85 for 85%). Returns null when missing.
 * @param {Object} item
 * @returns {number|null}
 */
export function resolveItemMarkupActualRatio(item) {
    return resolveItemPercentRatio(item, 'markupActual', 'markup_actual');
}

/** @type {BulkFieldDefinition[]} */
export const BULK_FIELD_DEFINITIONS = [
    {
        key: 'search',
        label: 'Căutare (cod/nume)',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'text',
        filterable: true,
        editable: false,
        readCurrent: (item) => `${item.product_code || ''} ${item.product_name || item.name || ''}`,
    },
    {
        key: 'productName',
        label: 'Denumire comercială',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'text',
        filterable: false,
        editable: true,
        readCurrent: (item) => asText(item.product_name || item.name),
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'status',
        label: 'Status',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'select',
        filterable: true,
        editable: true,
        allowCustom: true,
        options: PRODUCT_STATUS_OPTIONS,
        readCurrent: (item) => asText(item.status),
        formatDisplay: (value) => formatProductStatus(value),
        normalizeValue: (value) => normalizeProductStatus(value),
    },
    {
        key: 'manufacturerProductName',
        label: 'Denumire furnizor',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'text',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => {
            const tabs = item?.pricing_detail?.furnizorPricings;
            const defaultTab = Array.isArray(tabs)
                ? (tabs.find((tab) => tab.isDefault) || tabs[0])
                : null;
            return asText(
                defaultTab?.manufacturerProductName
                || item.manufacturer_product_name
                || item?.pricing_detail?.manufacturerProductName,
            );
        },
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'manufacturerProductCode',
        label: 'Cod produs furnizor',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'text',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => {
            const tabs = item?.pricing_detail?.furnizorPricings;
            const defaultTab = Array.isArray(tabs)
                ? (tabs.find((tab) => tab.isDefault) || tabs[0])
                : null;
            return asText(
                defaultTab?.manufacturerProductCode
                || item.manufacturer_product_code
                || item?.pricing_detail?.manufacturerProductCode,
            );
        },
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'measurementUnit',
        label: 'U.M.',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'measurementUnit',
        filterable: true,
        editable: true,
        readCurrent: (item) => asText(item.measurement_unit),
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'weight',
        label: 'Greutate',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'text',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => asText(item?.pricing_detail?.weight || item.weight),
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'productNotes',
        label: 'Note produs',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'text',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => asText(
            item?.pricing_detail?.productNotes
            || item?.pricing_detail?.product_notes
            || item.productNotes,
        ),
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'nomenclatureCode',
        label: 'Cod nomenclator',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'text',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => asText(
            item?.pricing_detail?.nomenclatureCode || item.nomenclature_code,
        ),
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'commercialPolicyDiscount',
        label: 'Discount politică comercială',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'percent',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(
            item,
            'commercialPolicyDiscount',
            'commercial_policy_discount',
        ),
        formatDisplay: asPercentDisplay,
    },
    {
        key: 'serialNumber',
        label: 'Serial nr.',
        group: BULK_FIELD_GROUPS.flags,
        valueType: 'boolean',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => Boolean(
            item.serial_number ?? item?.pricing_detail?.serialNumber,
        ),
        formatDisplay: asBooleanDisplay,
    },
    {
        key: 'isCompositeProduct',
        label: 'Produs compus',
        group: BULK_FIELD_GROUPS.flags,
        valueType: 'boolean',
        filterable: false,
        // Composite setup needs the product builder (components list) — not bulk.
        editable: false,
        requiresPricingDetail: true,
        readCurrent: (item) => Boolean(
            item?.pricing_detail?.isCompositeProduct || item.is_composite_product,
        ),
        formatDisplay: asBooleanDisplay,
    },
    {
        key: 'systems',
        label: 'Sisteme',
        group: BULK_FIELD_GROUPS.categories,
        valueType: 'systems',
        filterable: true,
        editable: false,
        options: MAIN_PRODUCT_CATEGORIES.map((category) => ({
            title: category.title,
            value: category.value,
        })),
        readCurrent: (item) => systemsFromItem(item),
        formatDisplay: (value) => {
            const list = Array.isArray(value) ? value : [value];
            const labels = list
                .map((entry) => normalizeSystemValue(entry))
                .filter(Boolean);
            return labels.length ? labels.join(', ') : '—';
        },
    },
    {
        key: 'systemSubcategory',
        label: 'Subsistem',
        group: BULK_FIELD_GROUPS.categories,
        valueType: 'text',
        filterable: true,
        editable: false,
        readCurrent: (item) => {
            const systems = systemsFromItem(item);
            if (!systems.length) {
                return '';
            }
            return subcategoryForSystem(item, systems[0]) || '';
        },
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'chapterCode',
        label: 'Categorie (capitol)',
        group: BULK_FIELD_GROUPS.categories,
        valueType: 'chapter',
        filterable: true,
        editable: false,
        requiresPricingDetail: true,
        readCurrent: (item) => asText(
            item?.pricing_detail?.chapterCode || item.chapter_code,
        ),
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'subchapterCode',
        label: 'Subcategorie (subcapitol)',
        group: BULK_FIELD_GROUPS.categories,
        valueType: 'text',
        filterable: false,
        editable: false,
        requiresPricingDetail: true,
        readCurrent: (item) => asText(
            item?.pricing_detail?.subchapterCode || item.subchapter_code,
        ),
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'manufacturerCode',
        label: 'Producător',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'manufacturer',
        filterable: true,
        editable: false,
        requiresPricingDetail: true,
        readCurrent: (item) => asText(
            item?.pricing_detail?.manufacturerCode || item.manufacturer_code,
        ),
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'manufacturerName',
        label: 'Denumire producător (catalog)',
        group: BULK_FIELD_GROUPS.details,
        valueType: 'text',
        filterable: true,
        editable: false,
        readCurrent: (item) => asText(
            item?.pricing_detail?.manufacturerName || item.manufacturer_name,
        ),
    },
    {
        key: 'visibleInOfferApp',
        label: 'Vizibil program ofertare',
        group: BULK_FIELD_GROUPS.flags,
        valueType: 'boolean',
        filterable: true,
        editable: true,
        readCurrent: (item) => Boolean(
            item.visible_in_offer_app
            ?? item?.pricing_detail?.visibleInOfferApp
            ?? true,
        ),
        formatDisplay: asBooleanDisplay,
    },
    {
        key: 'isDiscountableProduct',
        label: 'Produs discountabil',
        group: BULK_FIELD_GROUPS.flags,
        valueType: 'boolean',
        filterable: true,
        editable: false,
        readCurrent: (item) => Boolean(item.is_discountable_product),
        formatDisplay: asBooleanDisplay,
    },
    {
        key: 'supplierListPriceOld',
        label: 'Preț listă furnizor (vechi)',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'number',
        filterable: false,
        editable: false,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(
            item,
            'supplierListPriceOld',
            'supplier_list_price_old',
        ),
        formatDisplay: (value) => {
            if (value === null || value === undefined || value === '') {
                return '—';
            }
            return asNumberDisplay(value, 4);
        },
    },
    {
        key: 'supplierListPriceNew',
        label: 'Preț listă furnizor',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'number',
        filterable: false,
        editable: true,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        // Bulk = % majorare/micșorare on current pret listă furnizor (not absolute).
        // Keeps adaos actual and recalculates selling price (same policy as curs valutar).
        supportsMultiplyPercent: true,
        fixedApplyMode: 'multiplyPercent',
        readCurrent: (item) => readPricingField(
            item,
            'supplierListPriceNew',
            'supplier_list_price_new',
        ),
        formatDisplay: (value) => {
            if (value === null || value === undefined || value === '') {
                return '—';
            }
            return asNumberDisplay(value, 4);
        },
    },
    {
        key: 'supplierListPriceMajorare',
        label: 'Majorare costuri',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'number',
        filterable: false,
        editable: true,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        // Bulk Majorare = Majorare costuri (sumă fixă RON/buc). Does NOT touch
        // Preț listă furnizor / the informational old→new % badge.
        appliesCostUpliftFixed: true,
        readCurrent: (item) => readCostUpliftFixedCurrent(item),
        formatDisplay: (value) => asNumberDisplay(value, 4),
    },
    {
        key: 'currency',
        label: 'Monedă',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'select',
        filterable: true,
        // Not a bulk editable property (redundant / handled per furnizor in builder).
        editable: false,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        options: CURRENCY_OPTIONS.map((option) => ({
            title: option.title || option.text || option.value,
            value: option.value,
        })),
        readCurrent: (item) => asText(
            readPricingField(item, 'currency') || 'ron',
        ).toLowerCase(),
        formatDisplay: (value) => asText(value).toUpperCase() || '—',
    },
    {
        key: 'paymentTermDays',
        label: 'Termen plată (zile)',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'number',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(
            item,
            'paymentTermDays',
            'payment_term_days',
        ),
        formatDisplay: (value) => {
            if (value === null || value === undefined || value === '') {
                return '—';
            }
            return String(value);
        },
    },
    {
        key: 'discount1',
        label: 'Discount 1',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'percent',
        filterable: false,
        editable: true,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(item, 'discount1'),
        formatDisplay: asPercentDisplay,
    },
    {
        key: 'discount2',
        label: 'Discount 2',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'percent',
        filterable: false,
        editable: true,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(item, 'discount2'),
        formatDisplay: asPercentDisplay,
    },
    {
        key: 'cashDiscount',
        label: 'Discount cash',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'percent',
        filterable: false,
        editable: false,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(item, 'cashDiscount', 'cash_discount'),
        formatDisplay: asPercentDisplay,
    },
    {
        key: 'marketingDiscount',
        label: 'Discount marketing',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'percent',
        filterable: false,
        editable: false,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(item, 'marketingDiscount', 'marketing_discount'),
        formatDisplay: asPercentDisplay,
    },
    {
        key: 'transportCostRate',
        label: 'Cost transport + alte taxe (%)',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'percent',
        filterable: false,
        editable: true,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(item, 'transportCostRate', 'transport_cost_rate'),
        formatDisplay: asPercentDisplay,
    },
    {
        key: 'stampDutyAmount',
        label: 'Taxă de timbru',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'number',
        filterable: false,
        editable: true,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(item, 'stampDutyAmount', 'stamp_duty_amount'),
        formatDisplay: (value) => {
            if (value === null || value === undefined || value === '') {
                return '—';
            }
            return asNumberDisplay(value);
        },
    },
    {
        key: 'markupReference',
        label: 'Adaos referință',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'percent',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(item, 'markupReference', 'markup_reference'),
        formatDisplay: asPercentDisplay,
    },
    {
        key: 'markupActual',
        label: 'Adaos actual',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'percent',
        filterable: false,
        editable: true,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        readCurrent: (item) => readPricingField(item, 'markupActual', 'markup_actual'),
        formatDisplay: asPercentDisplay,
    },
    {
        // List-level rates only — change via Cursuri listă (recalc pe toată lista).
        // Per-product bulk edits desync calculations across the price list.
        key: 'exchangeRateEur',
        label: 'Curs EUR',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'number',
        filterable: false,
        editable: false,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        isListExchangeRate: true,
        readCurrent: (item) => readExchangeRateFromItem(item, 'exchangeRateEur'),
        formatDisplay: (value) => asNumberDisplay(value, 4),
    },
    {
        key: 'exchangeRatePln',
        label: 'Curs PLN',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'number',
        filterable: false,
        editable: false,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        isListExchangeRate: true,
        readCurrent: (item) => readExchangeRateFromItem(item, 'exchangeRatePln'),
        formatDisplay: (value) => asNumberDisplay(value, 4),
    },
    {
        key: 'exchangeRateUsd',
        label: 'Curs USD',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'number',
        filterable: false,
        editable: false,
        needsPricingRecalc: true,
        requiresPricingDetail: true,
        isListExchangeRate: true,
        readCurrent: (item) => readExchangeRateFromItem(item, 'exchangeRateUsd'),
        formatDisplay: (value) => asNumberDisplay(value, 4),
    },
    {
        key: 'notes',
        label: 'Note pret',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'text',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => asText(readPricingField(item, 'notes')),
        formatDisplay: (value) => asText(value) || '—',
    },
    {
        key: 'discountNotes',
        label: 'Note - Discount',
        group: BULK_FIELD_GROUPS.pricing,
        valueType: 'text',
        filterable: false,
        editable: true,
        requiresPricingDetail: true,
        readCurrent: (item) => asText(readPricingField(item, 'discountNotes', 'discount_notes')),
        formatDisplay: (value) => {
            const notes = parseStoredDiscountNotes(value);
            const joined = notes.map((entry) => String(entry ?? '').trim()).filter(Boolean).join(' · ');
            return joined || '—';
        },
    },
];

const FIELD_BY_KEY = BULK_FIELD_DEFINITIONS.reduce((map, field) => {
    map[field.key] = field;
    return map;
}, {});

export function getBulkField(key) {
    return FIELD_BY_KEY[key] || null;
}

export function getEditableBulkFields() {
    return BULK_FIELD_DEFINITIONS.filter((field) => field.editable);
}

export function getFilterableBulkFields() {
    return BULK_FIELD_DEFINITIONS.filter((field) => field.filterable);
}

export function getEditableBulkFieldOptions() {
    return getEditableBulkFields().map((field) => ({
        title: field.label,
        value: field.key,
        group: field.group,
        label: field.label,
    }));
}

/** Filter keys always shown in the bulk dialog (not added via +). */
export const CORE_BULK_FILTER_KEYS = [
    'search',
    'systems',
    'systemSubcategory',
    'manufacturerCode',
    'chapterCode',
    'subchapterCode',
    'furnizorLabel',
];

/**
 * Collect display labels for furnizor tabs on an item (for filtering).
 * @param {Object} item
 * @returns {string[]}
 */
export function getItemFurnizorFilterLabels(item) {
    const detail = item?.pricing_detail || {};
    const manufacturerName = asText(detail.manufacturerName || item?.manufacturer_name);
    const manufacturerCode = asText(detail.manufacturerCode || item?.manufacturer_code);
    const pricings = Array.isArray(detail.furnizorPricings) ? detail.furnizorPricings : [];
    if (!pricings.length) {
        const fallback = manufacturerName || manufacturerCode || asText(item?.furnizor);
        return fallback ? [fallback] : [];
    }
    const labels = [];
    for (const pricing of pricings) {
        if (pricing?.usesManufacturerAsFurnizor) {
            // Same keys the Produse API filter accepts for manufacturer-as-furnizor.
            if (manufacturerName) {
                labels.push(manufacturerName);
            }
            if (manufacturerCode) {
                labels.push(manufacturerCode);
            }
            continue;
        }
        const name = asText(pricing?.furnizorName);
        const code = asText(pricing?.furnizorCode);
        if (name) {
            labels.push(name);
        }
        if (code) {
            labels.push(code);
        }
    }
    return [...new Set(labels)];
}

/**
 * Properties that can be added as optional filters in the bulk dialog.
 * @returns {Array<{ title: string, value: string, group: string, label: string }>}
 */
export function getAddableBulkFilterOptions() {
    const core = new Set(CORE_BULK_FILTER_KEYS);
    return BULK_FIELD_DEFINITIONS
        .filter((field) => (
            !core.has(field.key)
            && field.key !== 'search'
            && (field.editable || field.filterable)
        ))
        .map((field) => ({
            title: field.label,
            value: field.key,
            group: field.group,
            label: field.label,
        }));
}

/**
 * Whether an item matches a single bulk-field filter value.
 * @param {Object} item
 * @param {BulkFieldDefinition} field
 * @param {unknown} wanted
 * @param {Record<string, unknown>} [criteria]
 * @returns {boolean}
 */
export function itemMatchesBulkFieldFilter(item, field, wanted, criteria = {}) {
    if (!field || wanted == null || wanted === '') {
        return true;
    }
    if (Array.isArray(wanted) && wanted.length === 0) {
        return true;
    }

    if (field.key === 'systems') {
        const wantedSystems = (Array.isArray(wanted) ? wanted : [wanted])
            .map((entry) => normalizeSystemValue(entry))
            .filter(Boolean);
        if (!wantedSystems.length) {
            return true;
        }
        const itemSystems = systemsFromItem(item);
        return wantedSystems.some((system) => itemSystems.includes(system));
    }

    if (field.key === 'systemSubcategory') {
        const wantedSub = String(wanted).trim().toLowerCase();
        const filterSystems = (
            Array.isArray(criteria.systems) ? criteria.systems : [criteria.systems]
        )
            .map((entry) => normalizeSystemValue(entry))
            .filter(Boolean);
        const systemsToCheck = filterSystems.length ? filterSystems : systemsFromItem(item);
        return systemsToCheck.some((system) => {
            const sub = subcategoryForSystem(item, system);
            return String(sub || '').trim().toLowerCase() === wantedSub;
        });
    }

    if (field.key === 'manufacturerCode' || field.valueType === 'manufacturer') {
        const wantedCode = String(wanted).trim().toLowerCase();
        const current = String(field.readCurrent(item) || '').trim().toLowerCase();
        if (!current) {
            return false;
        }
        // Compare numeric codes without leading zeros.
        if (/^\d+$/.test(wantedCode) && /^\d+$/.test(current)) {
            return Number(wantedCode) === Number(current);
        }
        return current === wantedCode;
    }

    if (field.key === 'chapterCode' || field.valueType === 'chapter') {
        const wantedChapters = (Array.isArray(wanted) ? wanted : [wanted])
            .map((entry) => String(entry || '').trim().toLowerCase())
            .filter(Boolean)
            .map((code) => (/^\d+$/.test(code) ? String(Number(code)) : code));
        if (!wantedChapters.length) {
            return true;
        }
        const currentRaw = String(field.readCurrent(item) || '').trim().toLowerCase();
        const current = /^\d+$/.test(currentRaw) ? String(Number(currentRaw)) : currentRaw;
        return Boolean(current) && wantedChapters.includes(current);
    }

    if (field.valueType === 'boolean') {
        return Boolean(field.readCurrent(item)) === Boolean(wanted);
    }

    if (field.valueType === 'percent') {
        const currentRaw = field.readCurrent(item);
        if (currentRaw === null || currentRaw === undefined || currentRaw === '') {
            return false;
        }
        const currentNumeric = parseNumericInput(currentRaw);
        const currentRatio = Math.abs(currentNumeric) <= 1.5
            ? currentNumeric
            : currentNumeric / 100;
        const wantedNumeric = parseNumericInput(wanted);
        const wantedRatio = Math.abs(wantedNumeric) > 0 && Math.abs(wantedNumeric) <= 1.5
            ? wantedNumeric
            : wantedNumeric / 100;
        return Math.abs(currentRatio - wantedRatio) < 0.0001;
    }

    if (field.valueType === 'number') {
        const currentRaw = field.readCurrent(item);
        if (currentRaw === null || currentRaw === undefined || currentRaw === '') {
            return false;
        }
        return parseNumericInput(currentRaw) === parseNumericInput(wanted);
    }

    if (
        field.valueType === 'select'
        || field.valueType === 'measurementUnit'
    ) {
        return String(field.readCurrent(item) || '').trim().toLowerCase()
            === String(wanted).trim().toLowerCase();
    }

    // Text and similar: diacritic-insensitive contains.
    const current = field.key === 'systemSubcategory'
        ? formatCurrentFieldValue(field, item, criteria)
        : field.readCurrent(item);
    const haystack = Array.isArray(current) ? current.join(' ') : current;
    return includesFolded(haystack, wanted);
}

/**
 * Compare percent values that may be stored as ratios (0.85) or percent points (85).
 * Uses the same 2-decimal display precision as the bulk table ("36%" == "36,00%"),
 * so tiny stored drift does not count as a change.
 * @param {unknown} left
 * @param {unknown} right
 * @returns {boolean}
 */
function percentValuesEqual(left, right) {
    const toRatio = (value) => {
        if (value === null || value === undefined || String(value).trim() === '') {
            return null;
        }
        const numeric = parseNumericInput(value);
        if (!Number.isFinite(numeric)) {
            return null;
        }
        return Math.abs(numeric) <= 1.5 ? numeric : numeric / 100;
    };
    const leftRatio = toRatio(left);
    const rightRatio = toRatio(right);
    if (leftRatio == null && rightRatio == null) {
        return true;
    }
    if (leftRatio == null || rightRatio == null) {
        return false;
    }
    const leftPercent = roundMoney(leftRatio * 100, BULK_NUMERIC_INPUT_MAX_DECIMALS);
    const rightPercent = roundMoney(rightRatio * 100, BULK_NUMERIC_INPUT_MAX_DECIMALS);
    return leftPercent === rightPercent;
}

/**
 * True when a bulk "Setează" value matches what the product already has,
 * so applying it would be a no-op (e.g. adaos actual 85% → 85%).
 * @param {BulkFieldDefinition|null|undefined} field
 * @param {Object} item
 * @param {unknown} value raw or already-normalized edit value
 * @returns {boolean}
 */
export function isBulkEditValueUnchanged(field, item, value) {
    if (!field || typeof field.readCurrent !== 'function') {
        return false;
    }
    const normalized = normalizeBulkEditValue(field, value);
    const current = field.readCurrent(item);

    if (field.valueType === 'percent') {
        if (percentValuesEqual(current, normalized)) {
            return true;
        }
        // Same label in the bulk table ("85%" → "85%") counts as unchanged even
        // when raw floats differ beyond ratio rounding edge cases.
        if (typeof field.formatDisplay === 'function') {
            const currentLabel = field.formatDisplay(current);
            const nextLabel = formatBulkEditValue(field, value);
            return Boolean(currentLabel)
                && currentLabel !== '—'
                && currentLabel === nextLabel;
        }
        return false;
    }
    if (field.valueType === 'boolean') {
        return Boolean(current) === Boolean(normalized);
    }
    if (field.valueType === 'number') {
        const currentText = String(current ?? '').trim();
        const nextText = String(normalized ?? '').trim();
        if (!currentText && !nextText) {
            return true;
        }
        const currentNum = parseNumericInput(current);
        const nextNum = parseNumericInput(normalized);
        return Number.isFinite(currentNum)
            && Number.isFinite(nextNum)
            && Math.abs(currentNum - nextNum) < 1e-6;
    }
    if (field.valueType === 'systems' || Array.isArray(normalized) || Array.isArray(current)) {
        const currentList = (Array.isArray(current) ? current : [current])
            .map((entry) => normalizeSystemValue(entry))
            .filter(Boolean)
            .sort();
        const nextList = (Array.isArray(normalized) ? normalized : [normalized])
            .map((entry) => normalizeSystemValue(entry))
            .filter(Boolean)
            .sort();
        return currentList.length === nextList.length
            && currentList.every((entry, index) => entry === nextList[index]);
    }
    return asText(current) === asText(normalized);
}

/**
 * Normalize a bulk value for comparison / writing into the builder form.
 * Percent fields are stored as percentage inputs (e.g. 85 for 85%).
 * @param {BulkFieldDefinition} field
 * @param {unknown} value
 * @returns {unknown}
 */
export function normalizeBulkEditValue(field, value) {
    if (!field) {
        return value;
    }
    if (typeof field.normalizeValue === 'function') {
        return field.normalizeValue(value);
    }
    if (field.valueType === 'boolean') {
        return Boolean(value);
    }
    if (field.valueType === 'systems') {
        const list = Array.isArray(value) ? value : [value];
        return [...new Set(
            list.map((entry) => normalizeSystemValue(entry)).filter(Boolean),
        )];
    }
    if (field.valueType === 'percent') {
        const numeric = parseNumericInput(value);
        // Accept either 85 or 0.85; store as percentage form for the builder.
        if (Math.abs(numeric) > 0 && Math.abs(numeric) <= 1.5) {
            return ratioToPercentageInput(numeric);
        }
        return String(value ?? '').trim() === '' ? '' : ratioToPercentageInput(numeric / 100);
    }
    if (field.valueType === 'number') {
        const numeric = parseNumericInput(value);
        if (String(value ?? '').trim() === '') {
            return '';
        }
        // Always store with comma decimals (ro-RO), max 2 fraction digits.
        return formatNumericInputValue(
            roundMoney(numeric, BULK_NUMERIC_INPUT_MAX_DECIMALS),
            BULK_NUMERIC_INPUT_MAX_DECIMALS,
        );
    }
    if (field.valueType === 'select'
        || field.valueType === 'measurementUnit'
        || field.valueType === 'manufacturer'
        || field.valueType === 'chapter'
    ) {
        return asText(value);
    }
    return asText(value);
}

/**
 * Format a field's current value for preview (handles percent ratios from API).
 * @param {BulkFieldDefinition} field
 * @param {Object} item
 * @param {Object} [criteria]
 * @returns {string}
 */
export function formatCurrentFieldValue(field, item, criteria = {}) {
    if (!field) {
        return '—';
    }
    if (field.key === 'systemSubcategory') {
        const system = criteria.systems;
        const selected = Array.isArray(system) ? system[0] : system;
        if (selected) {
            return subcategoryForSystem(item, selected) || '—';
        }
        const current = field.readCurrent(item);
        return asText(current) || '—';
    }
    const current = field.readCurrent(item);
    if (
        field.key === 'supplierListPriceNew'
        || field.key === 'supplierListPriceOld'
    ) {
        return formatSupplierListPriceBulkLabel(current, item);
    }
    if (field.formatDisplay) {
        return field.formatDisplay(current);
    }
    if (Array.isArray(current)) {
        return current.length ? current.join(', ') : '—';
    }
    return asText(current) || '—';
}

export function formatBulkEditValue(field, value) {
    if (!field) {
        return '—';
    }
    if (field.formatDisplay) {
        if (field.valueType === 'percent') {
            const normalized = normalizeBulkEditValue(field, value);
            return field.formatDisplay(parseNumericInput(normalized) / 100);
        }
        const normalized = typeof field.normalizeValue === 'function'
            ? field.normalizeValue(value)
            : value;
        return field.formatDisplay(normalized);
    }
    if (Array.isArray(value)) {
        return value.length ? value.join(', ') : '—';
    }
    return asText(value) || '—';
}

const BULK_DIRECT_LIST_SAVE_EXTRA_KEYS = new Set([
    'notes',
    'paymentTermDays',
    'manufacturerProductName',
    'manufacturerProductCode',
    'markupReference',
]);

/**
 * Bulk fields that may be written straight into the price list
 * (Detalii produs, note furnizor, termen de plată, adaos referință).
 * Price fields stay draft-only.
 * @param {string} fieldKey
 * @returns {boolean}
 */
export function isBulkDirectListSaveField(fieldKey) {
    const key = String(fieldKey || '').trim();
    if (!key) {
        return false;
    }
    if (isProductDetailsDirectSaveField(key) || BULK_DIRECT_LIST_SAVE_EXTRA_KEYS.has(key)) {
        return true;
    }
    const field = getBulkField(key);
    return Boolean(field?.editable && field.group === BULK_FIELD_GROUPS.details);
}

/**
 * Direct list save for one or more of the allowlisted fields, including an
 * optional furnizor note. Same multi-field rows as „Salvează în lucru”.
 * @param {Array<{ fieldKey?: string }>} editRows already-validated edit rows
 * @param {{ effectiveNote?: string }} [options]
 * @returns {boolean}
 */
export function bulkEditsAllowDirectListSave(editRows = [], { effectiveNote = '' } = {}) {
    const rows = Array.isArray(editRows) ? editRows : [];
    const hasNote = Boolean(String(effectiveNote || '').trim());
    if (!rows.length && !hasNote) {
        return false;
    }
    return rows.every((row) => isBulkDirectListSaveField(row?.fieldKey));
}

export { systemsFromItem, subcategoryForSystem, resolveItemMarkupRatio };
